'use client';

import { useLayoutEffect, useRef } from 'react';
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  type VirtualElement,
} from '@floating-ui/dom';

/**
 * Anchored-popover **positioning** (the behaviour half is `usePopover`): keeps
 * a floating panel glued to its trigger with `position: fixed` coordinates from
 * Floating UI, so the panel escapes any scrollable ancestor's clip - the
 * Dialog's scrolling body was cutting off Select/EntityField menus near its
 * bottom edge (the board item "Known gaps (July): UI and accessibility", found
 * 2026-07-03). Fixed positioning is not clipped by `overflow`, `flip()` opens
 * upward when there is no room below, and `shift()` keeps the panel inside the
 * viewport; `autoUpdate` re-anchors on scroll and resize while open.
 *
 * Floating UI is the design system's sanctioned positioning helper (a pure
 * helper that imposes no style - DESIGN_SYSTEM.md). Styling stays entirely in
 * the consumer's CSS module; this hook only writes coordinates.
 *
 * Wire-up: put `anchorRef` on the trigger, `floatingRef` on the panel, give
 * the panel `position: fixed` in its module, and render it only while open.
 * One panel serving several triggers (a tag chip opening its own path) passes
 * the pressed trigger as `anchor` instead; the panel re-anchors when it changes.
 * A panel anchored to a point in text (the prose editor's reference list, at
 * the caret) passes a Floating UI `VirtualElement` as `anchor`: its rect is
 * read on every update, and its `contextElement` carries the scroll parents.
 */
export function useAnchored<A extends HTMLElement, F extends HTMLElement>(
  open: boolean,
  {
    align = 'start',
    gap = 4,
    matchWidth = false,
    anchor,
  }: {
    /** Which trigger edge the panel's edge hugs (flip/shift still adjust). */
    align?: 'start' | 'end';
    /** Trigger-to-panel distance in px. */
    gap?: number;
    /** Give the panel the trigger's width as min-width (a Select listbox). */
    matchWidth?: boolean;
    /** The trigger element, when it is not the one `anchorRef` holds, or a
     *  virtual one (a caret's rect). `matchWidth` needs an element. */
    anchor?: A | VirtualElement | null;
  } = {},
) {
  const anchorRef = useRef<A | null>(null);
  const floatingRef = useRef<F | null>(null);

  useLayoutEffect(() => {
    const trigger = anchor ?? anchorRef.current;
    const floating = floatingRef.current;
    if (!open || !trigger || !floating) return;
    return autoUpdate(trigger, floating, () => {
      if (matchWidth && trigger instanceof HTMLElement) {
        floating.style.minWidth = `${trigger.offsetWidth}px`;
      }
      void computePosition(trigger, floating, {
        strategy: 'fixed',
        placement: align === 'end' ? 'bottom-end' : 'bottom-start',
        middleware: [offset(gap), flip({ padding: 8 }), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        floating.style.left = `${x}px`;
        floating.style.top = `${y}px`;
      });
    });
  }, [open, align, gap, matchWidth, anchor]);

  return { anchorRef, floatingRef };
}
